import apiClient from '@skygroup/shared/API/apiClient';
import {
  UseQueryResult,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import endpoints from '../../../../../../utils/endpoints';
import URLSearchParamsCreator from '../../../../../../utils/URLSearchParamsCreator';
import { removeFalsyValues } from '../../../../../../utils/objectCleaners';
import { RootState } from '../../../../../../store/store';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { has, isArray, isEmpty } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { message } from 'antd';
import { GEO_HOTEL_RADIUS } from './useHotelDataManagementByGeo';
import useMaxStayLimitGuard from '../../../../../../hooks/useMaxStayLimitGuard';
import { unwrapHotelSearchBody } from '../../../../../../utils/hotelSearchError';

export const useHotelSearch = ({
  searchParams,
  side_filter,
  return_side_filter_only,
  enabled = true,
  is_dp,
}: {
  searchParams: Record<string, any>;
  side_filter?: number;
  return_side_filter_only?: number;
  enabled?: boolean;
  is_dp?: 0 | 1;
}): UseQueryResult<any, Error> => {
  const queryClient = useQueryClient();
  const { to, from, multi_room_guests } = searchParams;
  const multiRoomGuests =
    typeof multi_room_guests === 'string' ? JSON.parse(multi_room_guests) : [];

  const urlParams = new URL(window.location.href).searchParams;

  const { siteConfig } = useSelector((data: RootState) => data.config.config);
  const { sideFilters } = useSelector((state: RootState) => {
    return state.search;
  });

  const [firstRoom, ...restOfTheRoom] = multiRoomGuests ?? [];

  const params = removeFalsyValues({
    from_date: moment(urlParams.get('from'), siteConfig.dateFormat).format(
      'YYYY-MM-DD'
    ),
    return_date: moment(urlParams.get('to'), siteConfig.dateFormat).format(
      'YYYY-MM-DD'
    ),
    // from_date: moment( from, siteConfig.dateFormat).format('YYYY-MM-DD'),
    // return_date: moment( to, siteConfig.dateFormat).format('YYYY-MM-DD'),
    region_id: urlParams.get('destinationCity'),
    adult: firstRoom?.adult,
    child: firstRoom?.child,
    infant: firstRoom?.infant,
    multi_room_guests: isEmpty(restOfTheRoom)
      ? false
      : JSON.stringify(restOfTheRoom),
    is_dp,
  });
  const extraDependencies = [
    urlParams.get('from'),
    urlParams.get('to'),
    urlParams.get('adult') || 0,
    urlParams.get('child') || 0,
    urlParams.get('infant') || 0,
  ];

  // Block requests beyond the Ratehawk 30-night cap (deep links / URL edits)
  const stayTooLong = useMaxStayLimitGuard(
    urlParams.get('from'),
    urlParams.get('to')
  );

  const query = useQuery<any, Error>(
    ['hotel', params, enabled, sideFilters, ...extraDependencies],
    async ({ signal }) => {
      // Cancel any previous pending request before making a new one

      // for some reason, the query is automatically cancelled
      // await queryClient.cancelQueries({ queryKey: ['hotel'] });

      const response = await apiClient.get(
        `${endpoints.HOTEL.SEARCH}?${URLSearchParamsCreator({
          ...params,
          ...(side_filter ? { side_filter: 1 } : {}),
          ...(return_side_filter_only ? { return_side_filter_only: 1 } : {}),
        })}`,
        { signal } // Attach the AbortSignal to cancel request if needed
      );

      // Surface provider errors (e.g. stay too long) instead of empty results
      return unwrapHotelSearchBody(response.data);
    },
    {
      enabled: enabled && !stayTooLong,
      refetchOnWindowFocus: false,
      keepPreviousData: false,
      // staleTime: Infinity,
      staleTime: 120000,
      cacheTime: 120000,
      retry: 2,
    }
  );

  return query;
};

export const useHotelSearchByIds = ({
  searchParams,
  side_filter,
  hotel_name = '',
  limit = 30,
  page = 1,
  enabled = false,
  is_dp,
}: {
  searchParams: Record<string, any>;
  side_filter?: number;
  hotel_name?: string;
  limit?: number | undefined;
  page?: number;
  enabled?: boolean;
  is_dp?: 0 | 1;
}): UseQueryResult<any, Error> => {
  const queryClient = useQueryClient();
  const { to, from, multi_room_guests } = searchParams;
  const multiRoomGuests =
    typeof multi_room_guests === 'string' ? JSON.parse(multi_room_guests) : [];

  const urlParams = new URL(window.location.href).searchParams;

  const { siteConfig } = useSelector((data: RootState) => data.config.config);
  const [firstRoom, ...restOfTheRoom] = multiRoomGuests ?? [];

  const params = removeFalsyValues({
    from_date: moment(urlParams.get('from'), siteConfig.dateFormat).format(
      'YYYY-MM-DD'
    ),
    return_date: moment(urlParams.get('to'), siteConfig.dateFormat).format(
      'YYYY-MM-DD'
    ),
    // from_date: moment( from, siteConfig.dateFormat).format('YYYY-MM-DD'),
    // return_date: moment( to, siteConfig.dateFormat).format('YYYY-MM-DD'),
    region_id: urlParams.get('destinationCity'),
    adult: firstRoom?.adult,
    child: firstRoom?.child,
    infant: firstRoom?.infant,
    // filter: isEmpty(filters) ? false : JSON.stringify(filters),
    multi_room_guests: isEmpty(restOfTheRoom)
      ? false
      : JSON.stringify(restOfTheRoom),
    is_dp,
  });

  const extraDependencies = [
    urlParams.get('from'),
    urlParams.get('to'),
    urlParams.get('adult'),
    urlParams.get('child'),
    urlParams.get('infant'),
  ];

  // Block requests beyond the Ratehawk 30-night cap (deep links / URL edits)
  const stayTooLong = useMaxStayLimitGuard(
    urlParams.get('from'),
    urlParams.get('to')
  );

  const query = useQuery<any, Error>(
    ['hotelByIds', params, hotel_name, page, enabled, ...extraDependencies],
    async ({ signal }) => {
      // Cancel any previous pending request before making a new one
      await queryClient.cancelQueries({ queryKey: ['hotelByIds'] });
      const response = await apiClient.get(
        `${endpoints.HOTEL.SEARCH_BY_IDS}?${URLSearchParamsCreator({
          ...params,
          ...(side_filter ? { side_filter: 1 } : {}),
          limit: limit,
          page: page,
        })}`,
        { signal } // Attach the AbortSignal to cancel request if needed
      );

      // Surface provider errors (e.g. stay too long) instead of empty results
      return unwrapHotelSearchBody(response.data);
    },
    {
      refetchOnWindowFocus: false,
      keepPreviousData: false,
      // enabled: enabled && emptyResCount <= 5, // if it returns empty [] 5 times we stop calling anymore
      enabled: enabled && !stayTooLong,
      // staleTime: Infinity,
      staleTime: 120000,
      cacheTime: 120000,
      retry: 2,
    }
  );

  return query;
};

export const useHotelSearchByGeo = ({
  searchParams,
  latitude,
  longitude,
  // radius The minimum value is 1.
  // The maximum value is 70 km (70000 meters).
  radius = GEO_HOTEL_RADIUS, // 30 km
  is_dp,
  enabled = false,
  // `limit` is not a page size on the geo path — the backend maps it to the
  // provider's hotels_limit (how many hotels serp/geo prices). Default 300.
  limit = 300,
  side_filter = 1,
}: {
  searchParams: Record<string, any>;
  latitude: number;
  longitude: number;
  radius?: number;
  is_dp?: 0 | 1;
  enabled?: boolean;
  limit?: number;
  side_filter?: number;
}): UseQueryResult<any, Error> => {
  const queryClient = useQueryClient();
  const { to, from, adult, child, infant, multi_room_guests } = searchParams;
  const multiRoomGuests =
    typeof multi_room_guests === 'string' ? JSON.parse(multi_room_guests) : [];

  const urlParams = new URL(window.location.href).searchParams;
  const { siteConfig } = useSelector((data: RootState) => data.config.config);
  const [firstRoom, ...restOfTheRoom] = multiRoomGuests ?? [];

  const params = removeFalsyValues({
    latitude: latitude,
    longitude: longitude,
    radius,
    from_date: moment(urlParams.get('from'), siteConfig.dateFormat).format(
      'YYYY-MM-DD'
    ),
    return_date: moment(urlParams.get('to'), siteConfig.dateFormat).format(
      'YYYY-MM-DD'
    ),
    adult: firstRoom?.adult || adult,
    child: firstRoom?.child || child,
    infant: firstRoom?.infant || infant,
    multi_room_guests: isEmpty(restOfTheRoom)
      ? false
      : JSON.stringify(restOfTheRoom),
    is_dp,
  });

  // Block requests beyond the Ratehawk 30-night cap (deep links / URL edits)
  const stayTooLong = useMaxStayLimitGuard(
    urlParams.get('from'),
    urlParams.get('to')
  );

  const query = useQuery<any, Error>(
    ['hotels-by-geo-location', params],
    async ({ signal }) => {
      // Cancel any previous pending request before making a new one
      // await queryClient.cancelQueries({
      //   queryKey: ['hotels-by-geo-location', params],
      // });
      const response = await apiClient.get(
        `${endpoints.HOTEL.SEARCH_BY_GEO}?${URLSearchParamsCreator({
          ...params,
          limit: limit,
          side_filter: side_filter,
        })}`,
        { signal } // Attach the AbortSignal to cancel request if needed
      );

      // Surface provider errors (e.g. stay too long) instead of empty results
      return unwrapHotelSearchBody(response.data);
    },
    {
      refetchOnWindowFocus: false,
      keepPreviousData: true,
      enabled: enabled && !stayTooLong,
      staleTime: 2 * 60 * 1000,
      cacheTime: 2 * 60 * 1000,
      retry: 2,
    }
  );

  return query;
};

export const useHotelSearchByCity = ({
  regionId,
  searchParams,
  is_dp,
  enabled = false,
}: {
  regionId: string | null;
  searchParams: Record<string, any>;
  is_dp?: 0 | 1;
  enabled?: boolean;
}): UseQueryResult<any, Error> => {
  const { siteConfig } = useSelector((data: RootState) => data.config.config);
  const urlParams = new URL(window.location.href).searchParams;
  const { multi_room_guests } = searchParams;
  const multiRoomGuests =
    typeof multi_room_guests === 'string' ? JSON.parse(multi_room_guests) : [];
  const [firstRoom, ...restOfTheRoom] = multiRoomGuests ?? [];

  const params = removeFalsyValues({
    from_date: moment(urlParams.get('from'), siteConfig.dateFormat).format(
      'YYYY-MM-DD'
    ),
    return_date: moment(urlParams.get('to'), siteConfig.dateFormat).format(
      'YYYY-MM-DD'
    ),
    region_id: regionId,
    adult: firstRoom?.adult,
    child: firstRoom?.child,
    infant: firstRoom?.infant,
    multi_room_guests: isEmpty(restOfTheRoom)
      ? false
      : JSON.stringify(restOfTheRoom),
    is_dp,
    side_filter: 1,
  });

  // Block requests beyond the Ratehawk 30-night cap (deep links / URL edits)
  const stayTooLong = useMaxStayLimitGuard(
    urlParams.get('from'),
    urlParams.get('to')
  );

  const query = useQuery<any, Error>(
    ['hotel-by-city', regionId, params, enabled],
    async ({ signal }) => {
      const response = await apiClient.get(
        `${endpoints.HOTEL.SEARCH}?${URLSearchParamsCreator(params)}`,
        { signal }
      );
      // Surface provider errors (e.g. stay too long) instead of empty results
      return unwrapHotelSearchBody(response.data);
    },
    {
      enabled: enabled && !!regionId && !stayTooLong,
      refetchOnWindowFocus: false,
      keepPreviousData: false,
      staleTime: 120000,
      cacheTime: 120000,
      retry: 2,
    }
  );

  return query;
};
